import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Eye as EyeIcon } from "@phosphor-icons/react/Eye";
import { EyeSlash as EyeSlashIcon } from "@phosphor-icons/react/EyeSlash";
import { paths } from "config/paths";
import { useFormik } from "formik";
import RouterLink from "next/link";
import * as React from "react";
import * as Yup from "yup";

import { MangoLogo } from "~mangomap-ui/templates/onboarding-template/fixtures/logo";
import { SplitLayout } from "~ui-components/components/organisms/split-layout";

export const SignUpV3Schema = Yup.object({
	name: Yup.string().required("Required"),
	password: Yup.string()
		.min(6, "Password is too short (minimum is 6 characters)")
		.required("Required"),
	acceptedToTermsAndPolicy: Yup.boolean().required().oneOf([true]),
});

export type SignUpV3FormValues = Yup.InferType<typeof SignUpV3Schema>;

export const SignUpV3RemoteSchema = Yup.object({
	email: Yup.string().email("Invalid email").required("Required"),
});

export type SignUpV3RemoteFormValues = Yup.InferType<
	typeof SignUpV3RemoteSchema
>;

export interface SignUpV3Props {
	form: ReturnType<typeof useFormik<SignUpV3FormValues>>;
	remoteForm: ReturnType<typeof useFormik<SignUpV3RemoteFormValues>>;
	onEmailChange: React.ChangeEventHandler<HTMLInputElement>;
	onEmailBlur: React.FocusEventHandler<HTMLInputElement>;
	isRemotelyValidatingEmail: boolean;
}

export const SignUpV3: React.FC<SignUpV3Props> = ({
	form,
	remoteForm,
	onEmailChange,
	onEmailBlur,
	isRemotelyValidatingEmail,
}) => {
	const [showPassword, setShowPassword] = React.useState(false);

	return (
		<SplitLayout
			title=""
			subtitle=""
			caption="Join 6,000+ forward-thinking companies:">
			<Stack
				spacing={4}
				component="form"
				onSubmit={form.handleSubmit}>
				<div>
					<Box
						component={RouterLink}
						href={paths.home}
						sx={{ display: "inline-block", fontSize: 0 }}>
						<MangoLogo />
					</Box>
				</div>
				<Typography variant="h5">Get started free</Typography>
				<Stack spacing={2}>
					<FormControl>
						<InputLabel>Name</InputLabel>
						<OutlinedInput
							autoFocus
							{...form.getFieldProps("name")}
							error={!!form.touched.name && !!form.errors.name}
						/>
						{!!form.touched.name && !!form.errors.name && (
							<FormHelperText error>{form.errors.name}</FormHelperText>
						)}
					</FormControl>
					<FormControl>
						<InputLabel>Email</InputLabel>
						<OutlinedInput
							type="email"
							name="email"
							value={remoteForm.values.email}
							onChange={onEmailChange}
							onBlur={onEmailBlur}
							error={!!remoteForm.touched.email && !!remoteForm.errors.email}
							endAdornment={
								isRemotelyValidatingEmail ? (
									<InputAdornment position="end">
										<CircularProgress size={24} />
									</InputAdornment>
								) : null
							}
						/>
						{!!remoteForm.touched.email && !!remoteForm.errors.email && (
							<FormHelperText error>{remoteForm.errors.email}</FormHelperText>
						)}
					</FormControl>
					<FormControl>
						<InputLabel>Password</InputLabel>
						<OutlinedInput
							type={showPassword ? "text" : "password"}
							{...form.getFieldProps("password")}
							error={!!form.touched.password && !!form.errors.password}
							endAdornment={
								<InputAdornment position="end">
									<IconButton
										edge="end"
										onClick={() => setShowPassword((prev) => !prev)}
										aria-label={
											showPassword ? "Hide password" : "Show password"
										}>
										{showPassword ? <EyeSlashIcon /> : <EyeIcon />}
									</IconButton>
								</InputAdornment>
							}
						/>
						{!!form.touched.password && !!form.errors.password && (
							<FormHelperText error>{form.errors.password}</FormHelperText>
						)}
					</FormControl>
					<div>
						<FormControlLabel
							control={
								<Checkbox {...form.getFieldProps("acceptedToTermsAndPolicy")} />
							}
							label={
								<React.Fragment>
									By clicking the &ldquo;Start trial&ldquo; button you agree to
									our <Link href={paths.terms}>Terms</Link> and&nbsp;
									<Link href={paths.privacy}>Privacy policy</Link>
								</React.Fragment>
							}
						/>
					</div>
					<Button
						type="submit"
						variant="contained"
						disabled={
							form.isSubmitting || !form.isValid || !remoteForm.isValid
						}>
						Start trial
					</Button>
					<Typography
						color="text.secondary"
						variant="body2">
						Already have an account?&nbsp;
						<Link
							href={paths.signIn}
							variant="subtitle2">
							Sign in
						</Link>
					</Typography>
				</Stack>
			</Stack>
		</SplitLayout>
	);
};
